// ─────────────────────────────────────────────────────────────────────────────
// Session — authentification et tenant actif.
//
// Le navigateur ne parle QU'À orion-id (login) et à core-api. Il ne touche
// JAMAIS les projets de données : c'est la raison d'être de la séparation des
// projets Supabase, et la clé anon utilisée ici n'y donne aucun accès.
//
// Le tenant actif vient des claims `orion.tenants` injectés dans le jeton par
// le hook `custom_access_token` — donc du serveur, pas d'un choix côté client
// qu'un utilisateur pourrait forger. On ne fait que CHOISIR parmi ce que le
// jeton autorise déjà ; core-api revérifie à chaque requête.
//
// ── Les organisations, lues aussi par l'API (1er octobre 2026) ──────────────
//
// Depuis que l'administrateur d'une organisation gère son équipe, le jeton a
// du retard : il vit une heure, et une organisation rejointe, un rôle changé
// n'y entrent qu'au suivant. `GET /v1/me` rend les organisations et l'accès
// tels que la base les voit, et c'est ce que l'écran montre dès qu'il les a.
// Le jeton reste la lecture de repli, au démarrage et si l'API ne répond pas.
// ─────────────────────────────────────────────────────────────────────────────
import { createClient, type Session } from '@supabase/supabase-js';
import { computed, ref } from 'vue';
// Sous-chemin et non le barrel : `@orion/domain` importe `node:crypto`,
// que le navigateur n'a pas. `claims` est pur.
import {
  canOpenModuleInTenant,
  hasRole,
  isPlatformSuperadmin,
  pickTenant,
  readOrionClaims,
  type TenantClaim,
} from '@orion/domain/claims';
import { estAdministrateur } from '@orion/domain/equipe';

const ID_URL = import.meta.env.VITE_ORION_ID_URL as string;
const ID_ANON = import.meta.env.VITE_ORION_ID_ANON_KEY as string;
const API_URL = ((import.meta.env.VITE_ORION_API_URL ?? import.meta.env.VITE_API_BASE_URL) as string | undefined) ?? '';

if (!ID_URL || !ID_ANON) {
  // Échouer FORT au démarrage : une app qui se charge avec une config
  // incomplète affiche un écran de login qui ne peut pas marcher, et
  // l'utilisateur croit à un mauvais mot de passe.
  throw new Error(
    'VITE_ORION_ID_URL et VITE_ORION_ID_ANON_KEY sont requis au build du studio.',
  );
}

export const supabase = createClient(ID_URL, ID_ANON, {
  auth: { persistSession: true, autoRefreshToken: true },
});

export type { TenantClaim };

/** Une organisation de la personne, et ce qu'elle y ouvre (lue par l'API). */
export interface OrganisationAcces {
  id: string;
  nom: string;
  role: string;
  /** `['*']` : superadmin de l'application, tous les modules. */
  modules: string[];
  administrateur: boolean;
  /**
   * Sa marque, quand l'ORGANISATION a le module « Marque de l'organisation »
   * (3 octobre 2026) : son logo s'il est déposé. `null` sinon.
   */
  marque?: { logo_url: string | null } | null;
}

const session = ref<Session | null>(null);
const activeTenantId = ref<string | null>(lireTenantMemorise());
const ready = ref(false);
/** `null` tant que l'API n'a pas répondu : on lit alors le jeton. */
const organisations = ref<OrganisationAcces[] | null>(null);
let derniereLecture = 0;

function lireTenantMemorise(): string | null {
  try {
    return localStorage.getItem('orion.tenant');
  } catch {
    return null;
  }
}

/**
 * Tenants portés par le JETON — la source d'autorité, pas une liste locale.
 *
 * Lus dans la CHARGE UTILE du jeton d'accès, où `custom_access_token_hook` les
 * écrit et où l'API les lit. Le décodage vit dans `@orion/domain` et il est
 * testé : chercher ces tenants dans `session.user.app_metadata` rendait
 * toujours une liste VIDE — supabase-js ne recopie pas les revendications
 * racines sur `session.user` — donc aucun en-tête `X-Orion-Tenant`, donc 400
 * sur toutes les routes de `/v1`, sur tous les écrans.
 */
const tenants = computed<TenantClaim[]>(
  () => readOrionClaims(session.value?.access_token).tenants,
);

const superadmin = computed(() => isPlatformSuperadmin(readOrionClaims(session.value?.access_token)));

/**
 * L'organisation active.
 *
 * Le choix mémorisé s'il est encore permis ; sinon celle que le jeton désigne
 * en premier (pour nos comptes, la nôtre, pas la première par ordre
 * alphabétique de toutes celles qu'ils voient) ; sinon la première.
 */
const activeTenant = computed<TenantClaim | null>(() => {
  const orgs = organisations.value;
  if (orgs !== null && orgs.length > 0) {
    const parJeton = pickTenant({ tenants: tenants.value }, null);
    const o =
      orgs.find((x) => x.id === activeTenantId.value)
      ?? orgs.find((x) => x.id === parJeton?.id)
      ?? orgs[0]!;
    return { id: o.id, role: o.role, modules: o.modules };
  }
  return pickTenant({ tenants: tenants.value }, activeTenantId.value);
});

const organisationActive = computed<OrganisationAcces | null>(
  () => organisations.value?.find((o) => o.id === activeTenant.value?.id) ?? null,
);

/**
 * Relit les organisations et l'accès auprès de l'API.
 *
 * Sans effet bloquant : un échec laisse la lecture du jeton en place. Bornée à
 * quelques secondes au démarrage, pour qu'une API lente ne retienne pas
 * l'affichage du studio.
 */
async function lireOrganisations(): Promise<void> {
  const token = session.value?.access_token;
  if (!token || API_URL === '') return;
  derniereLecture = Date.now();
  try {
    const r = await fetch(`${API_URL.replace(/\/$/, '')}/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(6000),
    });
    if (!r.ok) return;
    const j = (await r.json()) as { data?: { organisations?: OrganisationAcces[] } };
    if (Array.isArray(j.data?.organisations)) organisations.value = j.data.organisations;
  } catch {
    // Le jeton suffit à continuer.
  }
}

// Revenir sur l'onglet relit l'accès : un administrateur a pu changer le rôle
// entre-temps. Une fois par minute au plus.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - derniereLecture > 60_000) void lireOrganisations();
  });
}

/** Ce module s'ouvre-t-il dans l'organisation active ? */
function canOpen(moduleKey: string): boolean {
  if (superadmin.value) return true;
  const o = organisationActive.value;
  if (o !== null) return o.modules.includes('*') || o.modules.includes(moduleKey);
  return canOpenModuleInTenant(
    readOrionClaims(session.value?.access_token),
    activeTenant.value?.id ?? null,
    'core',
    moduleKey,
  );
}

export function useSession() {
  return {
    session: computed(() => session.value),
    user: computed(() => session.value?.user ?? null),
    tenants,
    activeTenant,
    /** Les organisations de la personne (toutes, pour nos comptes). */
    organisations: computed(() => organisations.value ?? []),
    organisationActive,
    /**
     * La marque de l'organisation active : son nom et son logo, dans la barre
     * latérale et en tête des livrables à imprimer. `null` quand
     * l'organisation n'a pas le module.
     */
    marque: computed<{ nom: string; logo_url: string | null } | null>(() => {
      const o = organisationActive.value;
      return o?.marque ? { nom: o.nom, logo_url: o.marque.logo_url } : null;
    }),
    ready: computed(() => ready.value),
    isAuthenticated: computed(() => session.value != null),

    /**
     * Peut-il ouvrir ce module ?
     *
     * Ce n'est PAS une securite : c'est un confort, masquer ce qui serait
     * refuse. L'autorite, c'est l'API, qui lit la meme regle en base.
     */
    canOpen,

    isSuperadmin: superadmin,

    /**
     * Administre-t-il l'équipe de l'organisation active ? Ses administrateurs,
     * et nous. C'est ce qui ouvre la page Équipe.
     */
    estAdministrateur: computed(() => superadmin.value || estAdministrateur(activeTenant.value?.role)),

    /**
     * Le rôle suffit-il pour ÉCRIRE ?
     *
     * `canOpen` répond « cet écran est-il à moi ? ». Il ne répond pas « ai-je
     * le droit d'y agir ? » — et l'API pose les deux gardes séparément :
     * `POST /v1/brands/:id/audits` exige le module `audits` ET le rôle
     * `analyst`. Un `viewer` à qui le module est accordé voyait donc l'écran
     * complet, bouton « audit profond » compris, et recevait 403 en cliquant.
     *
     * Ce n'est PAS une sécurité. C'est l'engagement de ne pas proposer ce qui
     * sera refusé.
     */
    canWrite: (min: 'analyst' | 'admin' | 'owner' = 'analyst'): boolean =>
      superadmin.value || hasRole(activeTenant.value, min),

    /** Le rôle effectif, pour l'EXPLIQUER dans une infobulle. */
    role: computed<string | null>(() => activeTenant.value?.role ?? null),

    /**
     * Les modules que ce compte peut ouvrir, pour les AFFICHER.
     *
     * Sert à l'écran d'accès refusé, qui liste ce que le compte a vraiment.
     * Un `SUPERADMIN` de plateforme n'a pas de liste — son droit est « tout ».
     */
    openModules: computed<string[]>(() => {
      if (superadmin.value) return ['(tous - SUPERADMIN)'];
      const o = organisationActive.value;
      if (o !== null) return o.modules.includes('*') ? ['(tous les modules de core)'] : o.modules;
      const claims = readOrionClaims(session.value?.access_token);
      const t = claims.tenants.find((x) => x.id === activeTenant.value?.id);
      if (t?.modules !== undefined) return t.modules;
      const grant = claims.apps.core;
      if (grant === undefined) return [];
      return grant.superadmin ? ['(tous les modules de core)'] : grant.modules;
    }),

    async init(): Promise<void> {
      const { data } = await supabase.auth.getSession();
      session.value = data.session;
      // Le jeton expire en une heure : sans cet abonnement, l'app garderait un
      // jeton mort et toutes les requêtes tomberaient en 401 sans explication.
      supabase.auth.onAuthStateChange((_event: string, s: Session | null) => {
        session.value = s;
        if (s === null) organisations.value = null;
      });
      // Les organisations AVANT le premier écran, sans le retenir plus de
      // deux secondes et demie : le jeton couvre le reste.
      if (data.session !== null) {
        await Promise.race([lireOrganisations(), new Promise((r) => setTimeout(r, 2500))]);
      }
      ready.value = true;
    },

    /**
     * Se connecter, et poser la session TOUT DE SUITE.
     *
     * La réponse de `signInWithPassword` PORTE la session : la lire ici évite
     * la fenêtre où l'app est connectée sans le savoir (le garde de navigation
     * renvoyait vers `/login`).
     */
    async signIn(email: string, password: string): Promise<void> {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      session.value = data.session;
      await lireOrganisations();
    },

    async signOut(): Promise<void> {
      await supabase.auth.signOut();
      try {
        localStorage.removeItem('orion.tenant');
      } catch {
        // Stockage indisponible : rien à oublier.
      }
      activeTenantId.value = null;
      organisations.value = null;
    },

    selectTenant(id: string): void {
      activeTenantId.value = id;
      try {
        localStorage.setItem('orion.tenant', id);
      } catch {
        // Le choix vaudra pour cet onglet.
      }
    },

    /**
     * Relire l'accès : après avoir rejoint une organisation, ou changé le
     * sien depuis la page Équipe. `jeton` force aussi un nouveau jeton, pour
     * que les appels qui le lisent encore le voient.
     */
    async rafraichir(jeton = false): Promise<void> {
      if (jeton) {
        const { data } = await supabase.auth.refreshSession();
        if (data.session) session.value = data.session;
      }
      await lireOrganisations();
    },
  };
}

/** Jeton FRAIS à chaque appel — jamais capturé une fois pour toutes. */
export async function currentToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function currentTenantId(): string | null {
  return activeTenant.value?.id ?? null;
}
